// Word Battle — a 4x4 Boggle-style letter grid. Dependency-free.

export const WB = {
  SIZE: 4,
  MIN_LEN: 3,
  MATCH_MS: 210_000,      // ~3.5 min
  SOLO_MS: 75_000,
  FREEZE_MS: 3000,
  STEAL_MS: 4000,
  /** words found to earn each power-up (index 0 -> first, 1 -> second, ...) */
  POWER_AT: [4, 9, 15, 22, 30],
  POWERS: ["freeze", "steal"] as const,
  /** every board is drawn until it hides at least this many everyday words
   *  (from dict-common.txt), so a good mix of short and long finds always exists */
  GUARANTEE: { total: 45, short: 25, five: 8, long: 4, seven: 1 },
  /** boards tried before settling for the best one seen */
  GUARANTEE_TRIES: 800,
} as const;

/** classic Boggle dice — 16 six-sided dice, one letter per face */
const DICE = [
  "AAEEGN", "ABBJOO", "ACHOPS", "AFFKPS",
  "AOOTTW", "CIMOTU", "DEILRX", "DELRVY",
  "DISTTY", "EEGHNW", "EEINSU", "EHRTVW",
  "EIOSST", "ELRTTY", "HIMNQU", "HLNNRZ",
];

export function scoreFor(len: number): number {
  if (len <= 2) return 0;
  return ({ 3: 1, 4: 2, 5: 4, 6: 7, 7: 11, 8: 16 } as Record<number, number>)[len] ?? 16 + (len - 8) * 5;
}

export function makeGrid(): string {
  const dice = [...DICE];
  // shuffle die order
  for (let i = dice.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [dice[i], dice[j]] = [dice[j]!, dice[i]!];
  }
  let g = "";
  for (const d of dice) g += d[(Math.random() * 6) | 0];
  // "QU" reads better than a lone Q on the board
  return g.replace(/Q/g, "Q");
}

/** are cells a and b (0..15) orthogonally or diagonally adjacent? */
export function adjacent(a: number, b: number, size = WB.SIZE): boolean {
  const ax = a % size, ay = (a / size) | 0;
  const bx = b % size, by = (b / size) | 0;
  const dx = Math.abs(ax - bx), dy = Math.abs(ay - by);
  return (dx | dy) !== 0 && dx <= 1 && dy <= 1;
}

/** validate a path is a simple chain of adjacent cells; returns the word or "" */
export function pathWord(grid: string, path: number[], size = WB.SIZE): string {
  if (path.length < WB.MIN_LEN) return "";
  const seen = new Set<number>();
  for (let i = 0; i < path.length; i++) {
    const c = path[i]!;
    if (c < 0 || c >= size * size || seen.has(c)) return "";
    if (i > 0 && !adjacent(path[i - 1]!, c, size)) return "";
    seen.add(c);
  }
  return path.map((c) => (grid[c] === "Q" ? "qu" : grid[c]!.toLowerCase())).join("");
}

/** every prefix of every word, so a board search can stop as soon as a path
 *  can't lead to a word */
export function prefixSet(dict: Iterable<string>): Set<string> {
  const pre = new Set<string>();
  for (const w of dict) for (let i = 1; i <= w.length; i++) pre.add(w.slice(0, i));
  return pre;
}

/** find words on the grid (for the bot and the board guarantee) — DFS with a
 *  dictionary probe; pass `prefixes` (from prefixSet) to search the whole
 *  board quickly */
export function findWords(
  grid: string, dict: Set<string>, size = WB.SIZE, cap = 40, maxLen = 8, prefixes?: Set<string>,
): { word: string; path: number[] }[] {
  const out: { word: string; path: number[] }[] = [];
  const seen = new Set<string>();
  const path: number[] = [];
  const used = new Array(size * size).fill(false);

  const dfs = (cell: number, word: string) => {
    if (out.length >= cap) return;
    const w = word + (grid[cell] === "Q" ? "qu" : grid[cell]!.toLowerCase());
    if (prefixes && !prefixes.has(w)) return;
    path.push(cell); used[cell] = true;
    if (w.length >= WB.MIN_LEN && w.length <= maxLen && !seen.has(w) && dict.has(w)) {
      seen.add(w);
      out.push({ word: w, path: [...path] });
    }
    if (w.length < maxLen) {
      for (let n = 0; n < size * size; n++) {
        if (!used[n] && adjacent(cell, n, size)) dfs(n, w);
      }
    }
    path.pop(); used[cell] = false;
  };

  for (let c = 0; c < size * size; c++) dfs(c, "");
  return out;
}

/** how a board's words split by length */
export function wordMix(words: { word: string }[]) {
  let short = 0, five = 0, long = 0, seven = 0;
  for (const { word } of words) {
    if (word.length <= 4) short++;
    else if (word.length === 5) five++;
    else long++;
    if (word.length >= 7) seven++;
  }
  return { total: words.length, short, five, long, seven };
}

/** a random board that meets WB.GUARANTEE against `common` (falls back to the
 *  richest board tried). Returns the board and every everyday word on it. */
export function makeGoodGrid(common: Set<string>, prefixes: Set<string>): {
  grid: string; words: { word: string; path: number[] }[];
} {
  const G = WB.GUARANTEE;
  let best: { grid: string; words: { word: string; path: number[] }[]; score: number } | null = null;
  for (let i = 0; i < WB.GUARANTEE_TRIES; i++) {
    const grid = makeGrid();
    const words = findWords(grid, common, WB.SIZE, 10_000, 8, prefixes);
    const m = wordMix(words);
    if (m.total >= G.total && m.short >= G.short && m.five >= G.five && m.long >= G.long && m.seven >= G.seven) {
      return { grid, words };
    }
    // how close it came, so the fallback is the nearest miss
    const score = Math.min(1, m.total / G.total) + Math.min(1, m.short / G.short) + Math.min(1, m.five / G.five)
      + Math.min(1, m.long / G.long) + Math.min(1, m.seven / G.seven);
    if (!best || score > best.score) best = { grid, words, score };
  }
  return { grid: best!.grid, words: best!.words };
}
